export type SettingsNavSearchLink = {
  label: string;
  /** Words for the settings on the link's page; see settings-search-words.ts. */
  searchWords: readonly string[];
  /**
   * Set by `filterSettingsNavLinks` on the links it keeps: the search word that explains the match when the link's
   * label and group do not, otherwise null.
   */
  searchHint?: string | null;
};

export type SettingsNavSearchGroup = {
  label: string;
  links: SettingsNavSearchLink[];
};

const queryTerms = (query: string): string[] => query.trim().toLowerCase().split(/\s+/).filter(Boolean);

/**
 * The search word holding the most of the terms its link's label and group do not, the first such on a tie; null
 * when the label and group hold every term.
 */
const searchHintFor = (searchWords: readonly string[], unexplainedTerms: string[]): string | null => {
  let hint: string | null = null;
  let hintTermCount = 0;
  for (const word of searchWords) {
    const lowerWord = word.toLowerCase();
    const termCount = unexplainedTerms.filter(term => lowerWord.includes(term)).length;
    if (termCount > hintTermCount) {
      hint = word;
      hintTermCount = termCount;
    }
  }
  return hint;
};

/**
 * Narrows the Settings menu to the links matching a query.
 *
 * A link stays when every word of the query appears in its label, its group's label or one of its search words,
 * in any order and any case, as in the Custom Outputs field picker (`matchesFieldQuery`). Groups left with no link
 * drop out. A blank query returns the input array itself. The result only ever holds input links in their input
 * groups, so it can never show a link the caller's permission and flag checks left out.
 */
export const filterSettingsNavLinks = <TGroup extends SettingsNavSearchGroup>(groups: TGroup[], query: string): TGroup[] => {
  const terms = queryTerms(query);
  if (terms.length === 0) {
    return groups;
  }

  return groups.flatMap(group => {
    const links = group.links.flatMap(link => {
      const name = `${link.label} ${group.label}`.toLowerCase();
      const unexplainedTerms = terms.filter(term => !name.includes(term));
      const searchWords = link.searchWords.map(word => word.toLowerCase());
      if (!unexplainedTerms.every(term => searchWords.some(word => word.includes(term)))) {
        return [];
      }
      return [{ ...link, searchHint: searchHintFor(link.searchWords, unexplainedTerms) }];
    });
    return links.length > 0 ? [{ ...group, links }] : [];
  });
};
